import { Injectable } from '@angular/core';
import { GetTimestampFromTime } from '@dave/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatMap, EMPTY, firstValueFrom, from, mergeMap, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { AppGatewayService } from '../../services/app-gateway.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { State } from '../index';
import { TextTemplateTagActionTypes } from '../actions/text-template-tag.actions';
import { TextTemplateTagEntityFromBackend } from '../../entities/text-template-tag.entity';
import { getTextTemplateTagDictionary } from '../selectors/text-template-tag.selector';


enum ErrorCodes {
    Add = 'Textvorlage Hinzufügen fehlgeschlagen',
    Load = 'Textvorlagen Abrufen fehlgeschlagen',
    Modify = 'Textvorlage Bearbeiten fehlgeschlagen',
    Remove = 'Textvorlage Löschen fehlgeschlagen',
}

@Injectable()
export class TextTemplateTagEffects {
    ModifyTextTemplateTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateTagActionTypes.Change),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ BaseTextTemplateTagEdit: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TextTemplateTagActionTypes.UpdateOne({ Payload: TextTemplateTagEntityFromBackend(res.BaseTextTemplateTagEdit.TextTemplateTag) });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Modify,
                                Err: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    DeleteTextTemplateTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateTagActionTypes.Delete),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ BaseTextTemplateTagDelete: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TextTemplateTagActionTypes.RemoveOne({ Payload: +Payload.Id });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Remove,
                                Err: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    AddTextTemplateTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateTagActionTypes.CreateRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ BaseTextTemplateTagAdd: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TextTemplateTagActionTypes.CreateSuccess({
                                Payload: TextTemplateTagEntityFromBackend(res.BaseTextTemplateTagAdd.TextTemplateTag),
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        TextTemplateTagActionTypes.CreateFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Errors: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    LoadTextTemplateTags = createEffect(() => {
        return this.actions$.pipe(
            ofType(TextTemplateTagActionTypes.Load),
            switchMap(({ Payload }) => {
                const requestPayload = isNotNullOrUndefined(Payload?.updatedSince) ? { UpdatedSince: Payload.updatedSince } : { };

                return from(this.gatewayService.Request({ BaseTextTemplateTagGet: requestPayload })).pipe(
                    mergeMap((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            if (!isNotNullOrUndefined(Payload.updatedSince)) {
                                return of(
                                    TextTemplateTagActionTypes.UpdateAll({
                                        Payload: res.BaseTextTemplateTagGet.TextTemplateTags.map((val) => TextTemplateTagEntityFromBackend(val)),
                                        updateLatestUpdatedAt: true,
                                    }),
                                );
                            } else if (res.BaseTextTemplateTagGet.TextTemplateTags.length) {
                                return from(firstValueFrom(this.store$.select(getTextTemplateTagDictionary))).pipe(
                                    mergeMap((TextTemplateTags) => {
                                        const filteredTextTemplateTags = res.BaseTextTemplateTagGet.TextTemplateTags.filter((TextTemplateTag) => {
                                            const fromState = TextTemplateTags[TextTemplateTag.Id];
                                            return !fromState || GetTimestampFromTime(fromState.UpdatedAt).toString() !== TextTemplateTag.UpdatedAt;
                                        });

                                        if (filteredTextTemplateTags.length) {
                                            return of(
                                                TextTemplateTagActionTypes.UpdateMany({
                                                    Payload: filteredTextTemplateTags.map((val) => TextTemplateTagEntityFromBackend(val)),
                                                    updateLatestUpdatedAt: true,
                                                }),
                                            );
                                        } else {
                                            return EMPTY;
                                        }
                                    }),
                                );
                            }
                            return EMPTY;
                        } else {
                            throw res.Errors;
                        }
                    }),
                    catchError((err) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Load,
                                    Err: err,
                                },
                            }),
                        ),
                    ),
                );
            }),
        );
    });
    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateTagActionTypes.CreateFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );
    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayService: AppGatewayService) {}
}
